import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';

const PackageViewer = () => {
  const [searchParams] = useSearchParams();
  const destination = searchParams.get('destination');
  const [iframeBlocked, setIframeBlocked] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const iframeRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!destination) return;

    // Detect iframe load failure
    const checkTimeout = setTimeout(() => {
      try {
        const iframe = iframeRef.current;
        if (iframe) {
          // Try to access iframe content - will throw if blocked by CORS/X-Frame-Options
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!doc || !doc.body || doc.body.innerHTML === '') {
            setIframeBlocked(true);
          }
        }
      } catch (e) {
        // Cross-origin error = iframe blocked
        setIframeBlocked(true);
      }
    }, 2000);

    return () => clearTimeout(checkTimeout);
  }, [destination]);

  // Handle iframe error event
  const handleIframeError = () => {
    setIframeBlocked(true);
  };

  // Countdown and redirect when blocked
  useEffect(() => {
    if (!iframeBlocked || !destination) return;

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          window.location.href = destination;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [iframeBlocked, destination]);

  return (
    <div className="h-screen flex flex-col overflow-hidden" data-testid="package-viewer-page">
      {/* Fixed Top Bar */}
      <div className="flex-shrink-0 h-16 bg-cream border-b border-maroon/15 flex items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center space-x-3" data-testid="viewer-logo-link">
          <img
            src="/logos/tamarin-dark.png"
            alt="Chimba Travel"
            className="h-10"
          />
          <span className="font-heading font-bold text-maroon text-lg sm:text-xl">Chimba Travel</span>
        </Link>

        <Link
          to="/partners"
          className="inline-flex items-center gap-2 text-maroon hover:text-terracotta font-semibold text-sm transition-colors"
          data-testid="back-to-site-btn"
        >
          <ArrowLeft size={18} />
          <span className="hidden sm:inline">Back to Travel Site</span>
          <span className="sm:hidden">Back</span>
        </Link>
      </div>

      {/* Content Area */}
      {!destination ? (
        <div className="flex-1 flex items-center justify-center bg-sand/30">
          <div className="text-center px-4">
            <p className="text-2xl font-heading font-bold text-maroon mb-2">No destination specified</p>
            <p className="text-maroon/60 mb-6">Add a <code className="bg-white px-2 py-1 rounded border border-maroon/10 text-sm">?destination=URL</code> parameter to view a partner page.</p>
            <Link to="/partners" className="btn-primary inline-flex items-center gap-2">
              <ArrowLeft size={16} />
              Browse Partners
            </Link>
          </div>
        </div>
      ) : iframeBlocked ? (
        /* Transition Page - iframe blocked */
        <div className="flex-1 flex items-center justify-center bg-cream" data-testid="transition-page">
          <div className="text-center px-4 max-w-md">
            <img
              src="/logos/tamarin-dark.png"
              alt="Chimba Travel"
              className="h-20 mx-auto mb-6"
            />
            <h2 className="text-2xl font-heading font-bold text-maroon mb-3">
              Taking you to our partner's secure booking site...
            </h2>
            <div className="flex items-center justify-center gap-2 mb-6">
              <div className="w-12 h-12 rounded-full border-4 border-terracotta/20 border-t-terracotta animate-spin"></div>
            </div>
            <p className="text-maroon/60 mb-2">
              Redirecting in <span className="font-bold text-terracotta text-lg">{countdown}</span> second{countdown !== 1 ? 's' : ''}
            </p>
            <a
              href={destination}
              className="inline-flex items-center gap-2 text-terracotta hover:text-terracotta/80 font-semibold text-sm mt-4 transition-colors"
              data-testid="redirect-now-link"
            >
              Go now <ExternalLink size={14} />
            </a>
          </div>
        </div>
      ) : (
        /* Iframe */
        <iframe
          ref={iframeRef}
          src={destination}
          title="Partner Package"
          className="flex-1 w-full border-0"
          style={{ height: 'calc(100vh - 4rem)' }}
          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation"
          onError={handleIframeError}
          data-testid="package-iframe"
        />
      )}
    </div>
  );
};

export default PackageViewer;
